import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { playlistsApi } from '../playlists/Playlists.api';
import { Playlist } from '../playlists/Playlists.types';
import { ScreenDetail } from './ScreenDetail';
import { screensApi } from './Screens.api';
import { MANAGE_SCREENS_PATH, Screen } from './Screens.types';

const REFRESH_MS = 7000;

/** One screen at `/#/beamonscreen/manage-screens/:screenId`. */
export const ScreenDetailPage: React.FC = () => {
    const { screenId = '' } = useParams<{ screenId: string }>();
    const navigate = useNavigate();
    const [screen, setScreen] = useState<Screen | null>(null);
    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
    const notify = (message: string, severity: 'success' | 'error' = 'success') => setSnackbar({ open: true, message, severity });

    const load = useCallback(async (quiet = false) => {
        if (!screenId) return;
        if (!quiet) setLoading(true);
        try {
            setScreen(await screensApi.get(screenId));
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load this screen');
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [screenId]);

    useEffect(() => {
        playlistsApi.list().then(setPlaylists).catch(() => undefined);
        void load();
        const t = setInterval(() => void load(true), REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: MANAGE_SCREENS_PATH, itemLabel: 'Manage Screens' },
                    { itemPath: `${MANAGE_SCREENS_PATH}/${screenId}`, itemLabel: screen?.name || 'Screen' },
                ]}
            />
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {loading && !screen ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress color="secondary" />
                </Box>
            ) : (
                <ScreenDetail
                    screen={screen}
                    playlists={playlists}
                    onChanged={(updated) => {
                        if (updated) setScreen(updated);
                        else void load(true);
                    }}
                    onRemoved={() => navigate(MANAGE_SCREENS_PATH)}
                    onMessage={notify}
                />
            )}
            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ScreenDetailPage;
